import mccCodesJson from "./mcc-codes.json";
import mccCodesStripeJson from "./mcc-codes-stripe.json";

export interface MccCode {
  value: string;
  label: string;
}

export const mccCodes: MccCode[] = mccCodesJson;

/**
 * Stripe Issuing's curated MCC list (~292 entries), sourced from
 * https://docs.stripe.com/issuing/categories. Used in v2 surfaces; v1 still
 * uses the full ISO list in `mccCodes`.
 */
export const mccCodesStripe: MccCode[] = mccCodesStripeJson;

const labelByCode = new Map<string, string>(
  mccCodes.map((c) => [c.value, c.label]),
);

const codeByLabel = new Map<string, string>(
  mccCodes.map((c) => [c.label, c.value]),
);

const stripeLabelByCode = new Map<string, string>(
  mccCodesStripe.map((c) => [c.value, c.label]),
);

export function getMccLabel(code: string): string | undefined {
  return labelByCode.get(code);
}

export function getMccCode(label: string): string | undefined {
  return codeByLabel.get(label);
}

export function getStripeMccLabel(code: string): string | undefined {
  return stripeLabelByCode.get(code);
}

/**
 * An MCC for display: the code, with its descriptive name beside it.
 *
 * Code first because it is the identifier — stable, searchable, and the same
 * across the ISO and Stripe lists, which give the same code different names.
 * The name is the gloss. An unrecognised code renders alone rather than as
 * "Unknown", since the code itself is still the useful fact.
 */
export function formatMcc(code: string | null | undefined): string {
  if (!code) return "";
  const label = getMccLabel(code);
  return label ? `${code} (${label})` : code;
}
